import { useEffect } from 'react';
import { X } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const SHORTCUT_GROUPS = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['G', 'H'], description: 'Go to Home' },
      { keys: ['G', 'B'], description: 'Go to Browse' },
      { keys: ['G', 'M'], description: 'Go to My Scholarships' },
      { keys: ['G', 'E'], description: 'Go to Essays' },
      { keys: ['G', 'P'], description: 'Go to Profile' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['/'], description: 'Focus search bar' },
      { keys: ['Ctrl', 'K'], description: 'Focus search bar' },
      { keys: ['S'], description: 'Save scholarship (on detail page)' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close dialog / overlay' },
    ],
  },
];

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-md bg-ds-gray-4/80 border border-ds-border text-ds-gray-1 text-xs font-mono font-medium">
      {children}
    </kbd>
  );
}

const KeyboardShortcutsHelp = ({ open, onClose }: KeyboardShortcutsHelpProps) => {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 bg-ds-card border border-ds-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 className="font-display text-lg font-semibold text-ds-gray-1">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-ds-gray-3 hover:text-ds-gray-1 hover:bg-ds-gray-4/50 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Shortcut groups */}
        <div className="px-6 pb-6 space-y-5">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-[11px] text-ds-gray-3 uppercase tracking-widest font-semibold mb-2.5">
                {group.title}
              </h3>
              <div className="space-y-2">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-ds-gray-1">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                      {shortcut.keys.map((key, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && (
                            <span className="text-[10px] text-ds-gray-3">
                              {group.title === 'Navigation' ? 'then' : '+'}
                            </span>
                          )}
                          <Kbd>{key}</Kbd>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-6 py-3 border-t border-ds-border bg-ds-bg/50">
          <p className="text-[11px] text-ds-gray-3 text-center">
            Shortcuts are disabled while typing in text fields
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
